'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Scale } from 'lucide-react';
import { useMountShuffledList } from './shuffle-utils';

interface TradeoffDecisionMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    scenario?: string;
    options?: any[];
    _hasHints?: boolean;
  };
}

export default function TradeoffDecisionMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: TradeoffDecisionMechanicProps) {
  const actualMechanicId = mechanicId || id || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={payload?.scenario || prompt || ''}
      icon={<Scale className="w-5 h-5" />}
      iconLabel="Tradeoff Decision — Weigh pros and cons of each option"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <TradeoffDecisionMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}


function TradeoffDecisionMechanicInner({ payload, submissionState, setAnswer }: any) {
        const [selectedOption, setSelectedOption] = useState<string | null>(null);
        const options = useMountShuffledList(payload?.options || []);

        useEffect(() => {
          if (selectedOption) {
            setAnswer({ selectedOptionId: selectedOption });
          } else {
            setAnswer(null);
          }
        }, [selectedOption, setAnswer]);

        return (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              {options.map((opt: any, idx: number) => {
                const id = opt.id || String(idx);
                const title = opt.content || opt.title || opt.id || "Option";
                const pros = Array.isArray(opt.pros) ? opt.pros : [];
                const cons = Array.isArray(opt.cons) ? opt.cons : [];
                
                return (
                  <button
                    type="button"
                    key={idx}
                    disabled={submissionState !== 'idle' && submissionState !== 'error'}
                    onClick={() => setSelectedOption(id)}
                    className={`text-left p-4 rounded-xl border-2 transition-all flex flex-col gap-3 ${
                      selectedOption === id 
                        ? 'border-primary bg-primary/5 shadow-sm' 
                        : 'border-border bg-background hover:border-primary/50'
                    }`}
                  >
                    <span className={`font-medium text-base leading-relaxed ${selectedOption === id ? 'text-primary' : 'text-foreground'}`}>
                      {title}
                    </span>
                    
                    {(pros.length > 0 || cons.length > 0) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 w-full">
                        {pros.length > 0 && (
                          <div className="flex flex-col gap-2 bg-green-500/5 dark:bg-green-500/10 p-3 rounded-lg border border-green-500/20">
                            <span className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wider flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Pros
                            </span>
                            <ul className="list-none text-sm text-foreground/80 flex flex-col gap-1.5">
                              {pros.map((pro: string, i: number) => (
                                <li key={i} className="flex gap-2">
                                  <span className="text-green-500 mt-0.5 opacity-70">+</span>
                                  <span className="flex-1">{pro}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {cons.length > 0 && (
                          <div className="flex flex-col gap-2 bg-red-500/5 dark:bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                            <span className="text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-wider flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Cons
                            </span>
                            <ul className="list-none text-sm text-foreground/80 flex flex-col gap-1.5">
                              {cons.map((con: string, i: number) => (
                                <li key={i} className="flex gap-2">
                                  <span className="text-red-500 mt-0.5 opacity-70">-</span>
                                  <span className="flex-1">{con}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
